import React, { useCallback, useState } from 'react';

import { FlatList, ListRenderItemInfo, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { RightArrowSettings } from 'assets/images';

import gStyles from 'config/styles';
import {
    BACK_BUTTON_ID,
    CREATE_MY_LIST_DESCRIPTION,
    CREATE_MY_LIST_START_BUTTON,
    CREATE_MY_LIST_TITLE,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import DialogView from 'components/Dialog';
import { ETextColor } from 'components/Text';

import SettingsRowItem from '../components/RowItem';
import WebHeader from '../components/WebHeader';
import useChannelsLineups from '../hooks/useChannelsLineups';
import {
    BACK_BUTTON,
    SETTINGS_CHANNELS_MY_LIST_DESCRIPTION_RN,
    SETTINGS_CHANNEL_LINEUP,
    SETTINGS_CHANNEL_LINEUP_CREATE_MY_LIST_RN,
    SETTINGS_CHANNEL_LINEUP_MY_LIST,
    SETTINGS_CHANNEL_LINEUP_STANDARD_LIST,
    SETTINGS_CHANNEL_SCAN_CHOOSE_BETWEEN_LISTS_BUTTON,
    START_BUTTON,
} from '../languagesConst';
import { ChannelsLineup } from '../types';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.CHANNELS_LINEUP
    >;
};

function ChannelLineup({ navigation }: Props) {
    const { t } = useTranslation();

    useHeaderConfig({ isRoot: isWeb, disableFocusOnMount: true });

    const { background, primary } = useSelector(selectLayoutConfig);

    const { myList, myListIsActive, channelsList } = useChannelsLineups();

    const [showCreateListDialog, setShowCreateListDialog] = useState(false);

    const handlePress = useCallback(
        (item: ChannelsLineup) => {
            if (item.screen) {
                navigation.navigate(item.screen);
            } else {
                setShowCreateListDialog(true);
            }
        },
        [navigation],
    );

    const handleBackButtonPress = useCallback(() => {
        setShowCreateListDialog(false);
    }, []);

    const handleStartButtonPress = useCallback(() => {
        navigation.navigate(SCREEN.EDIT_MY_LIST);
        setTimeout(handleBackButtonPress, 300);
    }, [handleBackButtonPress]);

    const renderChannelSwitchSubtitle = useCallback(
        (itemId: string) => {
            if (itemId === SETTINGS_CHANNEL_SCAN_CHOOSE_BETWEEN_LISTS_BUTTON) {
                if (myListIsActive) {
                    return t(SETTINGS_CHANNEL_LINEUP_MY_LIST);
                }

                return t(SETTINGS_CHANNEL_LINEUP_STANDARD_LIST);
            }

            return undefined;
        },
        [myListIsActive],
    );

    const renderItem = useCallback(
        ({ item, index }: ListRenderItemInfo<ChannelsLineup>) => (
            <SettingsRowItem
                item={item}
                title={t(item.title)}
                onPress={handlePress}
                RightIcon={
                    item.id ===
                    SETTINGS_CHANNEL_SCAN_CHOOSE_BETWEEN_LISTS_BUTTON
                        ? undefined
                        : RightArrowSettings
                }
                disabled={
                    item.id ===
                        SETTINGS_CHANNEL_SCAN_CHOOSE_BETWEEN_LISTS_BUTTON &&
                    !myList
                }
                titleColor={
                    item.id ===
                        SETTINGS_CHANNEL_SCAN_CHOOSE_BETWEEN_LISTS_BUTTON &&
                    !myList
                        ? ETextColor.dark_grey
                        : ETextColor.neutral
                }
                subtitle={renderChannelSwitchSubtitle(item.id)}
                position={index + 1}
                total={channelsList.length}
            />
        ),
        [t, handlePress, myList, renderChannelSwitchSubtitle],
    );

    return (
        <>
            {isWeb && (
                <WebHeader
                    title={t(SETTINGS_CHANNEL_LINEUP)}
                    disableFocusOnMount
                />
            )}
            <View
                style={[
                    gStyles.settingsContainer,
                    { backgroundColor: background },
                ]}
            >
                <FlatList
                    style={[
                        gStyles.settingsContentContainer,
                        { backgroundColor: background },
                    ]}
                    data={channelsList}
                    renderItem={renderItem}
                    keyExtractor={keyExtractor}
                    accessibilityRole="menu"
                />
            </View>

            <DialogView
                visible={showCreateListDialog}
                style={[styles.dialog, { backgroundColor: background }]}
                title={SETTINGS_CHANNEL_LINEUP_CREATE_MY_LIST_RN}
                titleTestID={CREATE_MY_LIST_TITLE}
                description={SETTINGS_CHANNELS_MY_LIST_DESCRIPTION_RN}
                descriptionTestID={CREATE_MY_LIST_DESCRIPTION}
                descriptionNumberOfLines={isTabletOrWeb ? 8 : 12}
                actionButtonTitle={BACK_BUTTON}
                actionButtonAccessibilityLabel={BACK_BUTTON}
                actionButtonFillColor={background}
                cancelButtonStyle={styles.button}
                cancelButtonTextStyle={styles.buttonText}
                actionButtonTestID={BACK_BUTTON_ID}
                cancelButtonTitle={START_BUTTON}
                cancelButtonAccessibilityLabel={START_BUTTON}
                cancelButtonFillColor={primary}
                closeButtonTestID={CREATE_MY_LIST_START_BUTTON}
                actionButtonOnPress={handleBackButtonPress}
                cancelButtonOnPress={handleStartButtonPress}
                actionButtonBordered
                cancelButtonBordered
                revertButtons
                onRequestClose={handleBackButtonPress}
            />
        </>
    );
}

export default ChannelLineup;

function keyExtractor(item: { id: string }) {
    return item.id;
}
